import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

// IntersectionObserver
function createObserver(targets, callback, options = { threshold: 0.5 }) {

  const observer = new IntersectionObserver(callback, options);
  targets.forEach((target) => observer.observe(target));
  return observer;
}

// Animate Function
function animateOnScroll(sectionClass) {
  const sections = document.querySelectorAll(`.${sectionClass}`);
  if (!sections.length) return;

  createObserver(
    sections,
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );
}

// FACTS and FIGURES
// KEY TARGET -> increase key values
function getDecimalPlaces(numberString) {
  const match = numberString.match(/[.,](\d+)/);
  return match ? match[1].length : 0;
}

function animateCounter(
  element,
  start,
  end,
  duration,
  decimalPlaces,
  useComma
) {
  let startTime = null;

  function step(timestamp) {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    const currentValue = start + (end - start) * progress;

    let displayValue = currentValue.toFixed(decimalPlaces);
    if (useComma) {
      displayValue = displayValue.replace('.', ',');
    }

    element.textContent = displayValue;

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

function initializeCounters() {
  const counters = document.querySelectorAll('.about__counterValue .value');
  if (!counters.length) return;

  createObserver(
    counters,
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          const targetValueString = counter.getAttribute('data-target');
          const useComma = targetValueString.includes(',');
          const targetValue = parseFloat(targetValueString.replace(',', '.'));
          const decimalPlaces = getDecimalPlaces(targetValueString);

          animateCounter(
            counter,
            0,
            targetValue,
            2000,
            decimalPlaces,
            useComma
          );
          observer.unobserve(counter);
        }
      });
    },
    { threshold: 0.5 }
  );
}

// MILESTONES
// KEY TARGETS - Functionality for milestones navigation
function initializeMilestones() {
  const milestonesSection = document.querySelector('.milestones');
  const timelineItems = Array.from(
    document.querySelectorAll('.milestones__timeline-item')
  );
  const milestonesItems = Array.from(
    document.querySelectorAll('.milestones__item')
  );

  if (!milestonesSection || !timelineItems.length || !milestonesItems.length) return;

  function adjustTimelineWidths(activeIndex) {
    timelineItems.forEach((item, index) => {
      const newWidth = Math.max(100 - Math.abs(index - activeIndex) * 10, 50);
      item.style.width = `${newWidth}%`;
    });
  }

  function getSectionPosition(section) {
    const rect = section.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      bottom: rect.bottom + window.scrollY,
    };
  }

  function addClassWhenInView(section, className) {
    const { top, bottom } = getSectionPosition(section);

    function checkUserPosition() {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const userPositionBottom = scrollY + viewportHeight;

      section.classList.toggle(
        className,
        scrollY >= top && userPositionBottom <= bottom
      );
    }

    window.addEventListener('scroll', checkUserPosition);
    checkUserPosition();
  }

  window.addEventListener('load', () => {
    addClassWhenInView(milestonesSection, 'in-view');
  });

  milestonesItems.forEach((item, index) => {
    createObserver(
      [item],
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            timelineItems.forEach((timelineItem) =>
              timelineItem.classList.remove('active')
            );
            timelineItems[index].classList.add('active');
            adjustTimelineWidths(index);
          }
        });
      },
      { threshold: 0.5 }
    );

    const handleTimelineClick = () => {
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
      timelineItems.forEach((timelineItem) =>
        timelineItem.classList.remove('active')
      );
      timelineItems[index].classList.add('active');
      adjustTimelineWidths(index);
    };

    timelineItems[index].addEventListener('click', handleTimelineClick);

    const handleMouseOver = () => adjustTimelineWidths(index);
    const handleMouseOut = () => {
      const activeIndex = timelineItems.findIndex((timelineItem) =>
        timelineItem.classList.contains('active')
      );
      adjustTimelineWidths(activeIndex);
    };

    timelineItems[index].addEventListener('mouseover', handleMouseOver);
    timelineItems[index].addEventListener('mouseout', handleMouseOut);
  });
}

// MILESTONE ITEMS UPDATE CLASSES
// KEY TARGET - added after loading classes for milestones items
function initializeMilestonesClasses() {
  const milestoneItems = document.querySelectorAll('.milestones__item');
  if (!milestoneItems.length) return;

  function updateMilestoneClasses() {
    const viewportTop = window.scrollY;
    const viewportBottom = viewportTop + window.innerHeight;

    milestoneItems.forEach((item) => {
      const itemTop = item.getBoundingClientRect().top + window.scrollY;
      const itemBottom = itemTop + item.offsetHeight;

      if (
        !item.classList.contains('scrollTop') &&
        !item.classList.contains('scrollBottom')
      ) {
        if (itemBottom <= viewportTop) {
          item.classList.add('scrollTop');
        } else if (itemTop >= viewportBottom) {
          item.classList.add('scrollBottom');
        } else {
          item.classList.add('scrollBottom');
        }
      }
    });
  }

  window.addEventListener('scroll', updateMilestoneClasses);
  updateMilestoneClasses();
}

// MILESTONES SECTION ANIMATION
// KEY TARGET - animation for the title and background image and ribbons
function initializeMilestonesAnimation() {
  const sections = document.querySelectorAll('.milestones__item');
  if (!sections.length) return;

  let lastScrollY = window.scrollY;

  function handleScrollDirection(entry) {
    const currentScrollY = window.scrollY;
    const direction = currentScrollY > lastScrollY ? 'down' : 'up';
    lastScrollY = currentScrollY;

    entry.target.classList.remove('visibleBottom', 'visibleTop');

    if (direction === 'down') {
      entry.target.classList.add('visibleBottom');
    } else if (direction === 'up') {
      entry.target.classList.add('visibleTop');
    }
  }

  createObserver(
    sections,
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          handleScrollDirection(entry);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );
}

// VIDEO SECTION
// KEY TARGET - functionality for sound switcher
function initializeVideoControls() {
  const video = document.querySelector('.video__bg video');
  const muteBtn = document.querySelector('.sound-off');
  const unmuteBtn = document.querySelector('.sound-on');

  if (!video || !muteBtn || !unmuteBtn) return;

  function muteVideo() {
    video.muted = true;
    muteBtn.style.display = 'none';
    unmuteBtn.style.display = 'flex';
  }

  function unmuteVideo() {
    video.muted = false;
    muteBtn.style.display = 'flex';
    unmuteBtn.style.display = 'none';
  }

  muteBtn.addEventListener('click', muteVideo);
  unmuteBtn.addEventListener('click', unmuteVideo);

  createObserver(
    [video],
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!video.muted) unmuteVideo();
        } else {
          muteVideo();
        }
      });
    },
    { threshold: 0.5 }
  );
}

// VIDEO SECTION
// KEY TARGET - open video section after loading page
function openVideoSection() {
  return new Promise((resolve) => {
    window.addEventListener('load', function () {
      const videoSection = document.querySelector('.video');

      if (!videoSection) return;

      if (videoSection) {
        const sectionPosition =
          videoSection.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: sectionPosition,
          behavior: 'auto',
        });

        requestAnimationFrame(() => {
          resolve();
        });
      } else {
        resolve();
      }
    });
  });
}

// TOP STORIES - ACCORDEON
// KEY TARGET - add functionality for the accordeon
function initializeAccordion() {
  const storiesItemsTrigger = document.querySelectorAll(
    '.stories__item--trigger'
  );

  if (!storiesItemsTrigger.length) return;

  storiesItemsTrigger.forEach((trigger) => {
    trigger.addEventListener('click', function () {
      // eslint-disable-next-line no-invalid-this
      const parentItem = this.parentElement;
      const content = parentItem.querySelector('.stories__item--content');
      const storiesItems = document.querySelectorAll('.stories__item');

      if (!storiesItems.length) return;

      storiesItems.forEach((item) => {
        const otherContent = item.querySelector('.stories__item--content');
        if (item !== parentItem) {
          item.classList.remove('active');
          otherContent.style.maxHeight = null;
        }
      });

      if (parentItem.classList.contains('active')) {
        content.style.maxHeight = null;
      } else {
        content.style.maxHeight = content.scrollHeight + 'px';
      }

      parentItem.classList.toggle('active');
    });
  });
}

// TOP STORIES - CAROUSEL
// KEY TARGET - develop custom carousel for answers
function initializeCarousel() {
  new Swiper('.stories__item--content', {
    modules: [Navigation, Pagination],
    pagination: {
      el: '.swiper-pagination',
      type: 'fraction',
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  });
}

// STAY TUNED SECTION PARALLAX
// KEY TARGET - add parallax effect for the section
function initializeParallax() {
  const stickySection = document.querySelector('.stayTuned');

  if (!stickySection) return;

  const coveredSection =
    stickySection.previousElementSibling?.tagName === 'SECTION'
      ? stickySection.previousElementSibling
      : null;

  if (!coveredSection) return;

  coveredSection.classList.add('covered');

  const handleIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const scrolledInsideStories =
          coveredSection.getBoundingClientRect().top * -1;

        coveredSection.style.top = `-${scrolledInsideStories}px`;
        coveredSection.classList.add('bottom');
        coveredSection.style.position = 'sticky';
      } else {
        coveredSection.style.top = '0';
        coveredSection.classList.remove('bottom');
        coveredSection.style.position = 'initial';
      }
    });
  };

  createObserver([stickySection], handleIntersect, { threshold: 0 });
}

// FOOTER
// KEY TARGET - develop custom select for choosing language
function initializeLanguageSelector() {
  const languageSelect = document.querySelector('.footer__language--wrapper');
  const languageList = document.querySelector('.footer__language--list');
  const footerLanguage = document.querySelector('.footer__language');

  if (!languageSelect || !languageList || !footerLanguage) {
    return;
  }

  let activeLanguage = null;

  languageSelect.addEventListener('click', () => {
    const isOpen = footerLanguage.classList.contains('open');
    footerLanguage.classList.toggle('open', !isOpen);
  });

  languageList.addEventListener('click', (e) => {
    if (e.target.tagName === 'LI') {
      if (activeLanguage) activeLanguage.classList.remove('active');
      activeLanguage = e.target;
      activeLanguage.classList.add('active');
      footerLanguage.classList.remove('open');
    }
  });

  document.addEventListener('click', (e) => {
    if (
      !languageSelect.contains(e.target) &&
      !languageList.contains(e.target)
    ) {
      footerLanguage.classList.remove('open');
    }
  });
}

// Next BEST sections
// KEY TARGET - calculate height of the image and set min-height for the section
function updateAllNextBestMinHeight() {
  const sections = document.querySelectorAll('.nextBest');

  sections.forEach((section) => {
    const imageBlock = section.querySelector('.nextBest__image');

    if (imageBlock) {
      const imageHeight = imageBlock.offsetHeight;
      section.style.minHeight = `${imageHeight}px`;
    }
  });
}

function removePreloader() {
  const preloader = document.querySelector('.preloader');
  if (preloader) {
    preloader.remove();
  }
}

// Initialize all functions during DOM loading
document.addEventListener('DOMContentLoaded', () => {
  openVideoSection().then(() => {
    initializeMilestonesClasses();
    animateOnScroll('video');
    animateOnScroll('celebrations');
    animateOnScroll('together');
    animateOnScroll('hero');
    animateOnScroll('sideBySide');

    removePreloader();
  });

  initializeCounters();
  initializeMilestones();
  initializeMilestonesAnimation();

  initializeVideoControls();

  initializeAccordion();
  initializeCarousel();

  initializeParallax();
  initializeLanguageSelector();

  updateAllNextBestMinHeight();
});

window.addEventListener('resize', () => {
  updateAllNextBestMinHeight();
});
